
interface Star {
  x: number;
  y: number;
  radius: number;
  speed: number;
  brightness: number;
  phase: number;
  phaseSpeed: number;
  hue: number;
}

interface ShootingStar {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  width: number;
}

const LAYER_COUNTS = [35, 55, 90];
const LAYER_COUNTS_MOBILE = [18, 28, 45];
const LAYER_SPEEDS = [0.15, 0.35, 0.7];
const LAYER_BRIGHTNESS = [0.5, 0.75, 1.0];
const LAYER_SIZE_MULT = [0.4, 0.65, 1.0];

const COLOR_STOPS: [number, number, number][] = [
  [233, 213, 255],
  [168, 85, 247],
  [34, 211, 238],
];

export class StarsBackground {
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private animationId: number | null = null;
  private isRunning = false;
  private layers: Star[][] = [];
  private shootingStars: ShootingStar[] = [];
  private resizeHandler: (() => void) | null = null;
  private shootingTimer = 0;
  private starCounts = LAYER_COUNTS;

  constructor(canvasId = 'stars-canvas') {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    this.canvas = canvas;
    this.ctx = ctx;
    this.setupCanvas();
    this.buildLayers();
    this.start();
  }

  private setupCanvas() {
    const resize = () => {
      const parent = this.canvas.parentElement;
      if (!parent) return;
      this.canvas.width = parent.clientWidth;
      this.canvas.height = parent.clientHeight;
    };
    resize();
    this.resizeHandler = resize;
    window.addEventListener('resize', resize);
  }

  private buildLayers() {
    this.layers = [];
    const w = this.canvas.width;
    const h = this.canvas.height;

    for (let l = 0; l < 3; l++) {
      const stars: Star[] = [];
      const count = this.starCounts[l];
      for (let i = 0; i < count; i++) {
        stars.push({
          x: Math.random() * w,
          y: Math.random() * h,
          radius: (Math.random() * 1.5 + 0.5) * LAYER_SIZE_MULT[l],
          speed: LAYER_SPEEDS[l],
          brightness: LAYER_BRIGHTNESS[l],
          phase: Math.random() * Math.PI * 2,
          phaseSpeed: 0.015 + Math.random() * 0.035,
          hue: Math.random(),
        });
      }
      this.layers.push(stars);
    }
  }

  private spawnShootingStar() {
    const w = this.canvas.width;
    const h = this.canvas.height;
    const angle = (Math.PI / 6) + Math.random() * (Math.PI / 4);
    const speed = 4 + Math.random() * 6;
    this.shootingStars.push({
      x: Math.random() * w * 0.8,
      y: Math.random() * h * 0.3,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      maxLife: 1,
      width: 1.2 + Math.random() * 1.5,
    });
  }

  private render() {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    for (const layer of this.layers) {
      for (const star of layer) {
        star.y += star.speed;
        if (star.y > h) { star.y = 0; star.x = Math.random() * w; }

        star.phase += star.phaseSpeed;
        const twinkle = 0.6 + 0.4 * Math.sin(star.phase);
        const alpha = star.brightness * twinkle;

        const [r1, g1, b1] = COLOR_STOPS[0];
        const [r2, g2, b2] = star.hue < 0.5 ? COLOR_STOPS[1] : COLOR_STOPS[2];

        const grad = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.radius * 3.5);
        grad.addColorStop(0, `rgba(${r1},${g1},${b1},${alpha})`);
        grad.addColorStop(0.4, `rgba(${r2},${g2},${b2},${alpha * 0.5})`);
        grad.addColorStop(1, `rgba(${r2},${g2},${b2},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius * 3.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(${r1},${g1},${b1},${alpha})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius * 0.45, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    this.shootingTimer += 1 / 60;
    if (this.shootingTimer > 3 + Math.random() * 5) {
      this.spawnShootingStar();
      this.shootingTimer = 0;
    }

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let i = this.shootingStars.length - 1; i >= 0; i--) {
      const s = this.shootingStars[i];
      s.x += s.vx;
      s.y += s.vy;
      s.life -= 0.012;

      if (s.life <= 0 || s.x > w || s.y > h) {
        this.shootingStars.splice(i, 1);
        continue;
      }

      const tailLen = 40 * (s.life / s.maxLife);
      const grad = ctx.createLinearGradient(s.x, s.y, s.x - s.vx * tailLen / 5, s.y - s.vy * tailLen / 5);
      grad.addColorStop(0, `rgba(233,213,255,${s.life * 0.9})`);
      grad.addColorStop(0.3, `rgba(168,85,247,${s.life * 0.5})`);
      grad.addColorStop(1, `rgba(34,211,238,0)`);

      ctx.strokeStyle = grad;
      ctx.lineWidth = s.width;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(s.x - s.vx * tailLen / 5, s.y - s.vy * tailLen / 5);
      ctx.stroke();
    }
    ctx.restore();
  }

  private animate = () => {
    if (!this.isRunning) return;
    this.render();
    this.animationId = requestAnimationFrame(this.animate);
  };

  start() {
    if (this.isRunning || !this.ctx) return;
    this.isRunning = true;
    this.animate();
  }

  stop() {
    this.isRunning = false;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  destroy() {
    this.stop();
    this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.layers = [];
    this.shootingStars = [];
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
  }

  setPerformanceMode(mobile: boolean) {
    const wasRunning = this.isRunning;
    this.stop();
    this.starCounts = mobile ? LAYER_COUNTS_MOBILE : LAYER_COUNTS;
    this.buildLayers();
    if (wasRunning) this.start();
  }
}

